import { useId, useMemo, useState } from 'react';
import { FileSpreadsheet, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import type { CsvImportDraft } from '@/lib/csv-import';
import { parseCsvFile } from '@/lib/csv-import';

interface CsvImportPickerProps {
  value: CsvImportDraft | null;
  onChange: (draft: CsvImportDraft | null) => void;
  title?: string;
  subtitle?: string;
  maxPreviewRows?: number;
  dataTestId?: string;
}

export function CsvImportPicker({
  value,
  onChange,
  title = 'Import CSV',
  subtitle = "Charge un CSV de leads. Les en-tetes proches de email / company_website / form_url seront normalises par le pipeline d'import.",
  maxPreviewRows = 3,
  dataTestId = 'csv-import-picker',
}: CsvImportPickerProps) {
  const inputId = useId();
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const previewRows = useMemo(
    () => value?.rows.slice(0, maxPreviewRows) ?? [],
    [maxPreviewRows, value],
  );

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    setIsParsing(true);
    try {
      const draft = await parseCsvFile(file);
      onChange(draft);
    } catch (err) {
      onChange(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsParsing(false);
      event.target.value = '';
    }
  }

  return (
    <div className="space-y-4 rounded border border-brand-border p-4" data-testid={dataTestId}>
      <div>
        <h3 className="text-sm font-semibold text-brand-dark">{title}</h3>
        <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor={inputId}>Fichier CSV</Label>
          <Input
            id={inputId}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            disabled={isParsing}
            data-testid={`${dataTestId}-input`}
          />
          <p className="text-xs text-gray-500">
            Formats d'en-tetes utiles: <code>email</code>, <code>company_name</code>, <code>company_website</code>, <code>form_url</code>, <code>first_name</code>, <code>last_name</code>.
          </p>
        </div>

        {isParsing && (
          <div className="rounded border border-brand-border bg-brand-surface px-3 py-2 text-sm text-gray-600">
            Lecture du CSV en cours...
          </div>
        )}

        {error && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {value && (
          <div className="space-y-3 rounded border border-emerald-200 bg-emerald-50/60 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm font-medium text-brand-dark">
                <FileSpreadsheet size={16} />
                {value.fileName}
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onChange(null)}
                data-testid={`${dataTestId}-clear`}
              >
                <X size={14} />
                Retirer
              </Button>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded border border-white/70 bg-white px-3 py-2">
                <div className="text-xs uppercase text-gray-400">Lignes</div>
                <div className="text-sm font-medium text-brand-dark">{value.rows.length}</div>
              </div>
              <div className="rounded border border-white/70 bg-white px-3 py-2">
                <div className="text-xs uppercase text-gray-400">Colonnes</div>
                <div className="text-sm font-medium text-brand-dark">{value.headers.length}</div>
              </div>
              <div className="rounded border border-white/70 bg-white px-3 py-2">
                <div className="text-xs uppercase text-gray-400">Separateur</div>
                <div className="text-sm font-medium text-brand-dark">
                  {value.delimiter === '\t' ? 'tab' : value.delimiter}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {value.headers.slice(0, 8).map((header) => (
                <span
                  key={header}
                  className="rounded-full bg-white px-2.5 py-1 text-xs text-gray-600 shadow-sm"
                >
                  {header}
                </span>
              ))}
              {value.headers.length > 8 && (
                <span className="rounded-full bg-white px-2.5 py-1 text-xs text-gray-500 shadow-sm">
                  +{value.headers.length - 8} autres
                </span>
              )}
            </div>

            <div className="overflow-x-auto rounded border border-white/70 bg-white">
              <table className="min-w-full text-xs">
                <thead className="border-b border-gray-100 bg-gray-50 text-left text-gray-500">
                  <tr>
                    {value.headers.slice(0, 6).map((header) => (
                      <th key={header} className="px-3 py-2 font-medium">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, rowIndex) => (
                    <tr key={`${value.fileName}-${rowIndex}`} className="border-b border-gray-100 last:border-b-0">
                      {value.headers.slice(0, 6).map((header) => (
                        <td key={`${rowIndex}-${header}`} className="px-3 py-2 text-gray-600">
                          {row[header] || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Upload size={12} />
              Ce fichier pourra etre importe a la creation de campagne ou plus tard depuis le detail campagne.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
